import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import classNames from "classnames"
import { Badge, Box, Divider, IconButton, Paper } from "@mui/material"
import { FilterList, ResetTvOutlined } from "@mui/icons-material"
import { isEmpty, isNil, some } from "lodash"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import CompareDateSelector from "connected-components/compare/components/date-selector"
import ListItemDropDown from "components/list-item-dropdown"
import OPTooltip from "components/material/tooltip"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useIsDashboardOptimizeExperimentsFeatureEnabled, useAccountAlias, useAccountId, useUserId } from "resources/account"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { OK, ERROR, DATA_STATUSES } from "helpers/data-statuses"
import { CUSTOM_PERIOD, SAME_DAY } from "helpers/dates"
import { computeIntradayAsOfDisplay } from "helpers/intraday"
import { createReduceDownloadData } from "./helpers/download-aggregator"
import { downloadRegistry } from "./context/download-registry"
import useIsConstituentsDownloadAllowed from "hooks/use-is-constituents-download-allowed"
import { getDate } from "modules/intraday/selectors/dates"
import { getOrCreateIntradaySocket } from "socket/intraday-connection"
import { useAutoRunOptimization } from "connected-components/optimize-experiments/hooks/use-auto-run-optimization"
import { getOptimizationResultExperimentName } from "connected-components/optimize-experiments/helpers/get-optimization-position-set"
import { useOptimizationPositionSet } from "connected-components/optimize-experiments/hooks/use-optimization-position-set"
import { MODEL_METADATA_SHAPE, INSTRUMENT_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import OptimizeExperimentsPanel from "connected-components/optimize-experiments/components/optimize-experiments-panel"
import { DashboardContext } from "./context/dashboard"
import DashboardModulesRenderer from "./components/dashboard-modules-renderer"
import DashboardDownload from "./components/download"
import FilterSelector from "./components/filter-selector"
import TradeManager from "./components/trader-manager"
import { getModeSelectorOptions } from "./helpers/mode-selector"
import { getDownloadDataFileName } from "./helpers/download"
import { getIndexPositionSets } from "./helpers/positions"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS, useDashboardLoadedTracking, useModeChangeTracking, useModelChangeTracking } from "./analytics"
import { useAdjustedDashboardDates } from "./hooks/dates"
import { useIntradayWarningNotification } from "./hooks/use-intraday-warning-notification"
import { useIntradayRefresh } from "./hooks/use-intraday-refresh"
import useMixPanel from "hooks/use-mixpanel"
import { useIsMixpanelSessionReplayEnabled } from "resources/account"
import IntradayDataInfo from "./components/intraday-data-info"
import LatestPriceDisplay from "./components/latest-price-display"
import DashboardTopBar from "./components/top-bar"
import { RULES } from "connected-components/compare/position-sets"
import { usePinArtifactHandler } from "./hooks/use-pin-artifact-handler"
import { useRemoveArtifactHandler } from "./hooks/use-remove-artifact-handler"
import { useModelId } from "./hooks/use-model-id"

import "./style.scss"

const PERIOD_SELECTOR_LABELS = {
  from: "Start",
  to: "End (As of)",
}

export default function Dashboard(props) {
  const {
    instrument: original,
    setInstrument, filter,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setComparisonInstrument,
    selectedMode,
    setSelectedMode,
    period,
    startDate,
    endDate,
    setPeriod,
    setBoundaryDates,
    useLatestDate,
    setUseLatestDate,
    setPerformanceStartDate,
    setPerformanceEndDate,
    isIntradayEnabled,
    isEndDateToday,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    intradayPricingAsOf,
    setIntradayPricingAsOf,
    isEndVsStartDateComparison,
    setIsEndDateToday,
    isPreferencesLoaded,
    setOptimizeConfiguration,
    optimizeConfiguration,
    setModelId,
    base,
    isOptimizePanelExpanded,
    setIsOptimizePanelExpanded,
    hasUserAppliedOptimization,
    optimizationSourceKind,
    optimizationExperimentSource,
  } = useContext(DashboardContext)
  const modelId = useModelId()
  const { modelMetadata, instrumentsWithMetadata, contentSets, hasMultipleInstruments, instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, lastIntradayBatch, lastIntradayBatchStatus, TradeContext, tradeContextValue } = props

  const analytics = useAnalytics()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const isMixpanelSessionReplayEnabled = useIsMixpanelSessionReplayEnabled()

  const mixpanelEventProperties = useMemo(() => ({
    source: "dashboard",
    account_id: accountId,
    account_alias: accountAlias,
    instrument_type: original?.type,
    instrument_id: original?.id,
  }), [accountId, accountAlias, original?.type, original?.id])

  useMixPanel({
    distinctId: isMixpanelSessionReplayEnabled ? userId : null,
    eventProperties: mixpanelEventProperties,
  })
  useDashboardLoadedTracking()
  useModeChangeTracking(selectedMode)
  useModelChangeTracking(modelId)

  const { resetTrades, trades } = tradeContextValue

  const optimizationPositionSet = useOptimizationPositionSet(
    original,
    optimizationSourceKind,
    optimizationExperimentSource,
  )

  const optimizationResultExperimentName = useMemo(
    () => getOptimizationResultExperimentName(optimizationSourceKind, optimizationExperimentSource),
    [optimizationSourceKind, optimizationExperimentSource],
  )

  const [filterButtonAnchor, setFilterButtonAnchor] = useState(null)
  const [filterSelectorStatus, setFilterSelectorStatus] = useState(false)

  const dockviewLayoutRef = useRef(null)

  const dispatch = useDispatch()
  const [layoutActions, setLayoutActions] = useState(null)
  const reduceDownloadData = useMemo(() =>
    createReduceDownloadData(() => layoutActions?.getPanelOrder?.() ?? []),
  [layoutActions],
  )

  useEffect(() => {
    return () => downloadRegistry.clear()
  }, [])
  const isIntradayBatchAvailable = !isEmpty(lastIntradayBatch) && lastIntradayBatchStatus === OK

  useIntradayWarningNotification()

  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isIntradayAnalyticsEnabled = useSelector(getIsIntradayAnalyticsEnabled)
  useAutoRunOptimization(optimizationPositionSet, modelId, endDate, base, setOptimizeConfiguration, optimizeConfiguration, setComparisonInstrument, isOptimizeExperimentsEnabled, isOptimizePanelExpanded, hasUserAppliedOptimization, optimizationResultExperimentName)

  const { currentAsOf, intradayRelativeDay } = useMemo(
    () => computeIntradayAsOfDisplay(intradayBatchMetadata),
    [intradayBatchMetadata],
  )

  const isIntradayInstrument = !!instrumentsWithMetadata?.original?.intradayEnabled

  const latestPriceTimestamp = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateToday || !intradayPricingAsOf) return null
    return moment(intradayPricingAsOf).format("LTS")
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateToday, intradayPricingAsOf])

  useEffect(() => {
    if (!isIntradayAnalyticsEnabled || isIntradayInstrument || !isEndDateToday) return
    const socket = getOrCreateIntradaySocket(accountId)

    function handleTimestampUpdate({ timestamp }) {
      setIntradayPricingAsOf(timestamp)
    }

    socket.on("timestampUpdated", handleTimestampUpdate)
    return () => {
      socket.off("timestampUpdated", handleTimestampUpdate)
    }
  }, [isIntradayAnalyticsEnabled, isIntradayInstrument, isEndDateToday, accountId, setIntradayPricingAsOf])

  const intradayDate = getDate()

  useEffect(() => {
    setIsEndDateToday(endDate === intradayDate)
  }, [endDate, intradayDate, setIsEndDateToday])

  const modeSelectorOptions = useMemo(() =>
    getModeSelectorOptions(rebalanced, comparison, isEndVsStartDateComparison),
  [rebalanced, comparison, isEndVsStartDateComparison])

  const handleFiltersToggle = useCallback((event) => {
    if (filterButtonAnchor) {
      analytics.track(DASHBOARD_EVENTS.FILTERS_CLOSED)
      setFilterButtonAnchor(null)
    } else {
      analytics.track(DASHBOARD_EVENTS.FILTERS_OPENED)
      setFilterButtonAnchor(event.currentTarget)
    }
  }, [filterButtonAnchor, setFilterButtonAnchor, analytics])

  const { adjustedStartDate, earliestDate, latestDate, unmodeledDates, datesAdjusted, isLoading: isLoadingDates } = useAdjustedDashboardDates({
    modelMetadata,
    instrumentsWithMetadata,
    period,
    startDate,
    endDate,
    useLatestDate,
    setUseLatestDate,
    setPeriod,
    setBoundaryDates,
    setPerformanceStartDate,
    setPerformanceEndDate,
    trackPeriodShortcutDateChanged: (oldStartDate, newStartDate, newEndDate, period) => analytics.track(DASHBOARD_EVENTS.DATE_SHORTCUT_CHANGED, {
      oldStartDate,
      newStartDate,
      newEndDate,
      period,
    }),
    instrument: original,
    isIntradayEnabled,
    isIntradayBatchAvailable,
    isIntradayAnalyticsEnabled,
  })

  const filtersCount = useMemo(() => {
    if (!filter) {
      return 0
    }
    return Object.keys(filter).length
  }, [filter])

  const isFiltersBadgeVisible = !filterButtonAnchor && filtersCount > 0

  useEffect(() => {
    if (instrumentMetadata?.unavailable) {
      setInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The main instrument could not be found and was reset."))
    } else if (comparisonInstrumentMetadata?.unavailable) {
      setComparisonInstrument(null)
      dispatch(NotificationCreators.add("warning", null, "The comparison instrument could not be found and was reset."))
    }
  }, [instrumentMetadata, setInstrument, setComparisonInstrument, comparisonInstrumentMetadata, dispatch])

  useEffect(() => {
    if (!isIntradayEnabled || !lastIntradayBatch || trades?.length > 0) {
      return
    }

    // Do not auto refresh if user is looking at historical data with intraday enabled
    if (isIntradayEnabled && isIntradayBatchAvailable && !isEndDateToday) {
      return
    }

    if (lastIntradayBatch.asOf !== intradayBatchMetadata?.asOf) {
      setIntradayBatchMetadata({
        date: lastIntradayBatch.date,
        asOf: lastIntradayBatch.asOf,
        id: lastIntradayBatch.id,
      })
    }
  }, [isIntradayEnabled, lastIntradayBatch, setIntradayBatchMetadata, intradayBatchMetadata, trades, isIntradayBatchAvailable, isEndDateToday])

  useEffect(() => {
    if (isEndVsStartDateComparison && original && comparison?.id !== original.id) {
      setComparisonInstrument(original)
    } else if (comparison?.type === RULES && comparison.rulesConfiguration.source.positionSet.id !== original.id) {
      setComparisonInstrument({
        type: RULES,
        rulesConfiguration: {
          source: { positionSet: { id: original.id, type: original.type } },
          rules: [...comparison.rulesConfiguration.rules],
        },
      })
    }
  }, [isEndVsStartDateComparison, setComparisonInstrument, original, comparison])

  useEffect(() => {
    if (!modeSelectorOptions || !selectedMode) {
      return
    }

    const isSelectedModeAvailable = some(modeSelectorOptions, option => option.id === selectedMode && !option.disabled)
    if (!isSelectedModeAvailable) {
      const newMode = modeSelectorOptions.find(option => !option.disabled)
      if (newMode) {
        setSelectedMode(newMode.id)
      }
    }
  }, [modeSelectorOptions, selectedMode, setSelectedMode])

  const setPeriodWithChange = useCallback((change) => {
    if (typeof change === "string") {
      setPeriod(change)
    } else {
      const newStartDate = change.adjusted?.startDate || change.startDate
      const newEndDate = change.adjusted?.endDate || change.endDate
      setPeriod({
        period: CUSTOM_PERIOD,
        startDate: newStartDate,
        endDate: newEndDate,
      })

      setUseLatestDate(newEndDate === intradayDate)
      analytics.track(DASHBOARD_EVENTS.CUSTOM_DATES_CHANGED, {
        oldStartDate: startDate,
        oldEndDate: endDate,
        newStartDate: newStartDate,
        newEndDate: newEndDate,
      })
    }
  }, [setPeriod, startDate, endDate, analytics, setUseLatestDate, intradayDate])

  const customizeOptions = useCallback((options) => [
    { value: SAME_DAY, label: "Same Day" },
    ...options,
  ], [])

  const handleDateChangeWithTradesCheck = useCallback((handleDateAction) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Date Change & Clear All Trades",
        contentText: "Changing the date will reset your trades. Would you still like to proceed with the date change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            handleDateAction()
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      handleDateAction()
    }
  }, [trades, dispatch, resetTrades, analytics])

  const handleModelChange = useCallback((newModelId) => {
    if (trades?.length > 0) {
      dispatch(DialogCreators.open({
        title: "Confirm Model Change & Clear All Trades",
        contentText: "Changing the model will reset your trades. Would you still like to proceed with the model change?",
        buttons: [{
          text: "Confirm",
          type: "primary",
          action: () => {
            setModelId(newModelId)
            resetTrades()
            analytics.track(DASHBOARD_EVENTS.TRADES_CLEARED)
          },
        }],
      }))
    } else {
      setModelId(newModelId)
    }
  }, [trades, dispatch, setModelId, resetTrades, analytics])

  const onDownloadData = useCallback(() => ({ data: reduceDownloadData(), fileName: getDownloadDataFileName(instrumentMetadata, isIntradayEnabled, latestPriceTimestamp) }), [reduceDownloadData, instrumentMetadata, isIntradayEnabled, latestPriceTimestamp])

  const [isDownloadAllowedStatus, isDownloadAllowed] = useIsConstituentsDownloadAllowed({ modelId, positionSets: getIndexPositionSets([instrumentMetadata, comparisonInstrumentMetadata]) })

  const hasOptimizationComparison = useMemo(() => {
    return !isNil(comparison) && isOptimizeExperimentsEnabled && comparison.isOptimization
  }, [comparison, isOptimizeExperimentsEnabled])

  const { showRefreshButton, showWarning, handleIntradayRefresh, refreshButtonTooltip } = useIntradayRefresh(
    isIntradayEnabled,
    isIntradayBatchAvailable,
    useLatestDate,
    lastIntradayBatch,
    intradayBatchMetadata,
    setIntradayBatchMetadata,
    setPeriod,
    period,
    adjustedStartDate,
    trades,
    resetTrades,
    onDownloadData,
    isPreferencesLoaded,
    isEndDateToday,
    lastIntradayBatchStatus,
    setUseLatestDate,
    setComparisonInstrument,
    setIsOptimizePanelExpanded,
    hasOptimizationComparison,
  )

  const handleSetPeriod = useCallback(change => {
    handleDateChangeWithTradesCheck(
      () => setPeriodWithChange(change),
    )
  }, [handleDateChangeWithTradesCheck, setPeriodWithChange])

  const handleResetEndDate = useCallback(() => {
    const resetDate = isIntradayEnabled ? intradayDate : latestDate

    setUseLatestDate(true)
    handleDateChangeWithTradesCheck(
      () => {
        setPeriod({
          period,
          endDate: resetDate,
          startDate: period === CUSTOM_PERIOD ? startDate : adjustedStartDate,
        })
        analytics.track(DASHBOARD_EVENTS.END_DATE_RESET_TO_LATEST, { oldEndDate: endDate, newEndDate: resetDate })
      },
    )
  }, [isIntradayEnabled, intradayDate, latestDate, setUseLatestDate, handleDateChangeWithTradesCheck, setPeriod, period, startDate, adjustedStartDate, analytics, endDate])

  const tooltipContent = <div>
    <div>Selected period: {period}</div>
    <br />
    <div><b>As of</b> date applies to point in time metrics; <b>Start</b> date used for performance, comparisons, & trends.</div>
  </div>

  return <TradeContext.Provider value={tradeContextValue}>
    <div className="modules_dashboard-root">
      <DashboardTopBar
        context={DashboardContext}
        instrumentMetadata={instrumentMetadata}
        comparisonInstrumentMetadata={comparisonInstrumentMetadata}
        baseInstrumentMetadata={baseInstrumentMetadata}
        adjustedStartDate={adjustedStartDate}
        onBaseSelected={(base, isDefault) => analytics.track(DASHBOARD_EVENTS.BASE_SELECTION, { base, isDefault })}
        onModelChange={handleModelChange}
      />
      <Paper square className="secondary-selectors">
        <div className="left-selectors">
          <CompareDateSelector {...{
            period: period,
            earliestDate: earliestDate,
            latestDate: latestDate,
            startDate: adjustedStartDate,
            endDate: endDate,
            onChange: handleSetPeriod,
            datesAdjusted,
            unmodeledDates,
            handleResetOption: handleResetEndDate,
            customizeOptions,
            isIntradayEnabled,
            isIntradayBatchAvailable,
            isEndDateToday,
            intradayAsOf: currentAsOf,
            intradayRelativeDay,
            intradayBatchStatus: lastIntradayBatchStatus,
            skipAutoAdjustment: true, // Prevent double adjustment since dashboard already computed adjustedStartDate
            datesWithLabels: true,
            labels: PERIOD_SELECTOR_LABELS,
            tooltipContent,
          }}
          />
          {isIntradayInstrument && (
            <IntradayDataInfo
              isIntradayEnabled={isIntradayEnabled}
              isEndDateToday={isEndDateToday}
              currentAsOf={currentAsOf}
              intradayRelativeDay={intradayRelativeDay}
              showWarning={showWarning}
              showRefreshButton={showRefreshButton}
              onRefresh={handleIntradayRefresh}
              refreshButtonTooltip={refreshButtonTooltip}
              isLoadingDates={isLoadingDates}
              useLatestDate={useLatestDate}
            />
          )}
          {isIntradayAnalyticsEnabled && !isIntradayInstrument && latestPriceTimestamp && (
            <LatestPriceDisplay timestamp={latestPriceTimestamp} />
          )}
          {!isEmpty(modeSelectorOptions) && <>
            <Divider orientation="vertical" flexItem />
            <ListItemDropDown
              disableSubHeader
              classes={{
                toggle: "mode-selector",
              }}
              label="Showing"
              value={selectedMode}
              setValue={setSelectedMode}
              options={modeSelectorOptions}
              alignMenu="left"
              disablePortal
            />
          </>}
          <Divider orientation="vertical" flexItem />
          <TradeManager context={TradeContext} instrumentsWithMetadata={instrumentsWithMetadata} />
          <OPTooltip title="Reset Layout">
            <IconButton
              sx={{ marginLeft: -1 }}
              onClick={() => {
                const actions = layoutActions ?? dockviewLayoutRef.current
                const api = actions?.getApi?.()
                if (api) {
                  actions.resetLayout(api)
                }
              }}
              size="large"
            >
              <ResetTvOutlined />
            </IconButton>
          </OPTooltip>
        </div>
        <div className="right-selectors">
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Divider orientation="vertical" flexItem />
            <DashboardDownload context={DashboardContext} id="dashboard-download-button" instrumentMetadata={instrumentMetadata} isIntradayEnabled={isIntradayEnabled} latestPriceTimestamp={latestPriceTimestamp} disabled={isDownloadAllowedStatus !== OK || !isDownloadAllowed} reduceDownloadData={reduceDownloadData} />
            <OPTooltip title={filterSelectorStatus === ERROR ? "Error loading filters. Applying filters is disabled." : "Filter positions"}>
              <span>
                <IconButton
                  onClick={handleFiltersToggle}
                  size="large"
                  color={filterButtonAnchor ? "primary" : undefined}
                  disabled={filterSelectorStatus !== OK}
                  data-test-id="filters-toggle"
                >
                  <Badge overlap="rectangular" color="primary" invisible={!isFiltersBadgeVisible} badgeContent={filtersCount}>
                    <FilterList />
                  </Badge>
                </IconButton>
              </span>
            </OPTooltip>
          </Box>
        </div>
      </Paper>
      <FilterSelector context={DashboardContext} setFilterSelectorStatus={setFilterSelectorStatus} hasMultipleInstruments={hasMultipleInstruments} anchor={filterButtonAnchor} toggleFilters={handleFiltersToggle} />
      <div className={classNames("dashboard-content", { "optimizer-open": isOptimizeExperimentsEnabled && isOptimizePanelExpanded })}>
        {isOptimizeExperimentsEnabled && (
          <OptimizeExperimentsPanel
            isOptimizedExperimentsEnabled={isOptimizeExperimentsEnabled}
            context={TradeContext}
            modelMetadata={modelMetadata}
            optimizationPositionSet={optimizationPositionSet}
            optimizationResultExperimentName={optimizationResultExperimentName}
          />
        )}
        <ModulesEventListeners />
        <div className="dashboard-main-content">
          <DashboardModulesRenderer
            TradeContext={TradeContext}
            instrumentsWithMetadata={instrumentsWithMetadata}
            hasMultipleInstruments={hasMultipleInstruments}
            contentSets={contentSets}
            modelMetadata={modelMetadata}
            dockviewLayoutRef={dockviewLayoutRef}
            onLayoutReady={setLayoutActions}
          />
        </div>
      </div>
    </div>
  </TradeContext.Provider>
}

function ModulesEventListeners() {
  usePinArtifactHandler()
  useRemoveArtifactHandler()
  return null
}

Dashboard.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  comparisonInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  baseInstrumentMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE),
  instrumentsWithMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  hasMultipleInstruments: PropTypes.bool.isRequired,
  lastIntradayBatch: PropTypes.object.isRequired,
  lastIntradayBatchStatus: PropTypes.oneOf(DATA_STATUSES).isRequired,
  TradeContext: PropTypes.object.isRequired,
  tradeContextValue: PropTypes.object.isRequired,
}
